import { useState, useCallback } from 'react';
import activityService from '../../../services/trips/trip-activity.service';
import { useTrip } from '../../contexts/trip-context';
import { ITripActivity } from '../../../Models/trips/trip-activity.model';

export interface IAddActivityModel {
  activityId: string;
  title: string;
  description: string;
  websiteUrl?: string;
  isPublic: boolean;
  imageUrl: string;
  tripId: string;
  type?: string;
  types?: string[];
  tags?: { tag: string; value?: string }[];
  placesId?: string;
  latitude?: number;
  longitude?: number;
  addressComponents?: any[];
}

export interface IAddTripActivityModel {
  activityId: string;
  title: string;
  description: string;
  websiteUrl?: string;
  isPublic: boolean;
  imageUrl: string;
  folderId: string | null;
  tripId: string;
  type?: string;
  types?: string[];
  tags?: { tag: string; value?: string }[];
  placesId?: string;
  latitude?: number;
  longitude?: number;
}

export interface IPlacesModel {
  activityId?: string;
  placesId: string;
  latitude: number;
  longitude: number;
  name: string;
  types?: string[];
  addressComponents?: any[];
}

export const useAddActivity = (
  tripId: string,
  parentFolderId: string,
  onSuccess?: () => void
) => {
  const [activity, setActivity] = useState<IAddActivityModel>({
    title: '',
    description: '',
    isPublic: false,
    imageUrl: 'default.jpg',
    tripId: tripId,
    activityId: '',
    tags: [],
    type: undefined,
    types: [],
    placesId: undefined,
    latitude: undefined,
    longitude: undefined,
    websiteUrl: undefined,
    addressComponents: []
  });
  const [imageUrl, setImageUrl] = useState('');
  const [validated] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { setRefreshTrip } = useTrip();

  const setPlace = useCallback(
    (place: any) => {
      if (place) {
        setActivity((prevActivity) => ({
          ...prevActivity,
          title: place.name,
          description: place.description,
          type: place.primaryType,
          types: place.types || [],
          isPublic: place.isPublic ?? false,
          placesId: place.id ?? undefined,
          latitude: place.latitude ?? undefined,
          longitude: place.longitude ?? undefined,
          websiteUrl: place.websiteUrl ?? undefined,
          activityId: place.activityId ?? '',
          folderId: place.folderId ?? parentFolderId,
          tags: place.tags ?? [],
          imageUrl: place.imageUrl || 'default.jpg', // Use place's imageUrl or default
          addressComponents: place.addressComponents || []
        }));
        setImageUrl(place.imageUrl || 'default.jpg'); // Set image URL from place or default
      }
    },
    [parentFolderId]
  );

  const handleInputChange = useCallback(
    (field: keyof IAddActivityModel, value: any) => {
      setActivity((prev) => ({
        ...prev,
        [field]: value
      }));
    },
    []
  );

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    e.stopPropagation();

    // Prevent duplicate submissions
    if (isSubmitting) {
      return;
    }

    setIsSubmitting(true);

    const addActivityModel: IAddActivityModel = {
      ...activity,
      imageUrl: imageUrl || 'default.png'
    };

    try {
      const result = await activityService.createActivity(
        tripId,
        addActivityModel
      );
      if (result) {
        alert('Activity created successfully');
        setRefreshTrip(true);
        setActivity({
          title: '',
          description: '',
          isPublic: false,
          imageUrl: 'default.jpg',
          tripId: tripId,
          activityId: '',
          tags: [],
          type: undefined,
          types: [],
          placesId: undefined,
          latitude: undefined,
          longitude: undefined,
          websiteUrl: undefined,
          addressComponents: []
        });
        onSuccess?.();
      } else {
        alert('Activity creation failed');
      }
    } catch (error) {
      console.error('Error creating activity:', error);
      alert('Error creating activity. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmitWithPlace = useCallback(
    async (
      placeData: IPlacesModel,
      addActivityToContext?: (activity: ITripActivity) => void,
      removeActivityFromContext?: (activityId: string) => void
    ) => {
      // Prevent duplicate submissions
      if (isSubmitting) {
        return;
      }

      setIsSubmitting(true);

      // Create optimistic activity object for instant UI update (same pattern as useActivityOperations)
      const optimisticActivity = {
        id: `temp-${Date.now()}`, // Temporary ID until server responds
        title: placeData.name,
        description: '',
        type: placeData.types?.[0],
        types: placeData.types || [],
        isPublic: false,
        googlePlaceId: placeData.placesId,
        latitude: placeData.latitude,
        longitude: placeData.longitude,
        addressComponents: placeData.addressComponents || [],
        imageUrl: 'default.png',
        tripId: tripId,
        deleted: false,
        createdAt: new Date(),
        updatedAt: new Date(),
        tags: [],
        websiteUrl: undefined,
        folderId: null,
        notes: []
      } as unknown as ITripActivity;

      // 1. Immediately add to UI (optimistic update - no page reload!)
      if (addActivityToContext) {
        console.log(
          '[useAddActivity] Adding activity optimistically:',
          optimisticActivity.title
        );
        addActivityToContext(optimisticActivity);
      }

      try {
        // 2. Make API call in background using createActivityFromPlace (better for place data)
        const result = await activityService.createActivityFromPlace(tripId, {
          googlePlaceId: placeData.placesId,
          placeData: {
            name: placeData.name,
            latitude: placeData.latitude,
            longitude: placeData.longitude,
            types: placeData.types,
            addressComponents: placeData.addressComponents
          }
        });

        // 3. Silent background refresh with delay (syncs server data imperceptibly)
        if (result?.status === 'created' || result?.status === 'reused') {
          console.log(
            '[useAddActivity] Activity created successfully, silent refresh'
          );
          // setTimeout(() => setRefreshTrip(true), 100);
          onSuccess?.();
          return result;
        } else {
          throw new Error('Activity creation failed');
        }
      } catch (error) {
        console.error('[useAddActivity] Error creating activity:', error);
        // Revert optimistic update without page reload
        if (removeActivityFromContext && optimisticActivity.id) {
          removeActivityFromContext(optimisticActivity.id);
        }
        throw error;
      } finally {
        setIsSubmitting(false);
      }
    },
    [tripId, isSubmitting, onSuccess]
  );

  return {
    activity,
    imageUrl,
    validated,
    isSubmitting,
    setPlace,
    handleInputChange,
    handleSubmit,
    handleSubmitWithPlace
  };
};
